import { useEffect, useMemo, useState } from "react";
import { auth, db } from "@/integrations/firebase/client";
import { doc, onSnapshot, collection, getDocs, getDoc, orderBy, query, limit, where } from "firebase/firestore";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { signOut } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from "recharts";
import { computeUpscaleCredits } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { getFirebaseFunctionUrl } from "@/lib/utils";
import { useToast as useUiToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

const Account = () => {
  const [userDoc, setUserDoc] = useState<any>(null);
  const [latestGens, setLatestGens] = useState<any[]>([]);
  const [active, setActive] = useState<'profile' | 'activity' | 'subscription'>('profile');
  const [timeRange, setTimeRange] = useState<'10d'|'30d'|'3m'|'6m'|'1y'>('10d');
  const navigate = useNavigate();
  const [buttonTitles, setButtonTitles] = useState<Record<string,string>>({});
  const [baseCosts, setBaseCosts] = useState<Record<string,number>>({});
  const { toast } = useToast();
  const uiToast = useUiToast();
  const [showChangePlan, setShowChangePlan] = useState<{plan:'classic'|'pro'|'free', mode:'upgrade'|'downgrade'}|null>(null);
  const [changing, setChanging] = useState(false);
  const [billingRows, setBillingRows] = useState<any[]>([]);
  const routeLocation = useLocation();

  useEffect(() => {
    if (!auth.currentUser) return;
    const ref = doc(db, 'users', auth.currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => setUserDoc(snap.data()));
    // Success message when redirected back from Stripe
    try {
      const url = new URL(window.location.href);
      const success = url.searchParams.get('success');
      const plan = url.searchParams.get('plan');
      if (success === 'true') {
        uiToast.toast({
          title: 'Abonnement activé',
          description: `Votre abonnement ${plan || ''} est actif. Les crédits seront ajoutés à chaque facture.`,
        });
        url.searchParams.delete('success');
        url.searchParams.delete('plan');
        window.history.replaceState({}, '', url.pathname);
      }
    } catch {}
    (async () => {
      // compute start date from selected range
      const now = new Date();
      const start = new Date(now);
      if (timeRange === '10d') start.setDate(now.getDate() - 10);
      if (timeRange === '30d') start.setDate(now.getDate() - 30);
      if (timeRange === '3m') start.setMonth(now.getMonth() - 3);
      if (timeRange === '6m') start.setMonth(now.getMonth() - 6);
      if (timeRange === '1y') start.setFullYear(now.getFullYear() - 1);
      const gensRef = collection(db, 'users', auth.currentUser.uid, 'generations');
      const qy = query(gensRef, where('created_at', '>=', start), orderBy('created_at', 'desc'), limit(500));
      const snap = await getDocs(qy);
      setLatestGens(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
      // Load billing history (last 12 invoices)
      try {
        const invRef = collection(db, 'users', auth.currentUser.uid, 'billing_invoices');
        const invQ = query(invRef, orderBy('created_at', 'desc'), limit(12));
        const invSnap = await getDocs(invQ);
        setBillingRows(invSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
      } catch {}
    })();
    return () => unsub();
  }, [timeRange]);

  // Deep link: select subscription tab when `?tab=subscription` or `#subscription`
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(routeLocation.search || '');
      const tab = String(searchParams.get('tab') || '').toLowerCase();
      const hash = String(routeLocation.hash || '').toLowerCase();
      if (tab === 'subscription' || hash.includes('subscription')) {
        setActive('subscription');
      }
    } catch {}
  }, [routeLocation.search, routeLocation.hash]);

  // Enrich missing titles/base costs for history items
  useEffect(() => {
    (async () => {
      if (!auth.currentUser) return;
      const idsToFetch = Array.from(new Set(
        (latestGens || [])
          .filter(g => (!g.button_title || !g.credits_cost) && typeof g.button_id === 'string')
          .map(g => g.button_id as string)
          .filter(id => !(id in buttonTitles) || !(id in baseCosts))
      ));
      if (idsToFetch.length === 0) return;
      const newTitles: Record<string,string> = {};
      const newCosts: Record<string,number> = {};
      for (const id of idsToFetch) {
        try {
          const ds = await getDoc(doc(db, 'enhancement_buttons', id));
          if (ds.exists()) {
            const data = ds.data() as any;
            if (data?.title) newTitles[id] = data.title;
            if (typeof data?.credits_cost === 'number') newCosts[id] = data.credits_cost;
          }
        } catch {}
      }
      if (Object.keys(newTitles).length) setButtonTitles(prev => ({ ...prev, ...newTitles }));
      if (Object.keys(newCosts).length) setBaseCosts(prev => ({ ...prev, ...newCosts }));
    })();
  }, [latestGens, buttonTitles, baseCosts]);

  const nextChargeMs = useMemo(() => {
    try {
      const ts = userDoc?.subscription_current_period_end;
      let ms: number | null = ts?.toDate ? ts.toDate().getTime() : (typeof ts?.seconds === 'number' ? ts.seconds * 1000 : null);
      if (!ms) {
        const latest = (billingRows || [])[0];
        const pe = latest?.period_end;
        if (pe) {
          ms = pe?.toDate ? pe.toDate().getTime() : (typeof pe?.seconds === 'number' ? pe.seconds * 1000 : null);
        }
      }
      return ms || null;
    } catch {
      return null;
    }
  }, [userDoc, billingRows]);

  const creditsUsed = useMemo(() => {
    const arr = latestGens || [];
    return arr.reduce((acc, g) => {
      if (typeof g.credits_cost === 'number') return acc + g.credits_cost;
      const base = typeof baseCosts[g.button_id] === 'number' ? baseCosts[g.button_id] : 0;
      const preset = (g.upscale_preset || 'original') as 'original'|'auto'|'auto_2k'|'auto_4k';
      const up = computeUpscaleCredits(preset as any);
      return acc + (base + up);
    }, 0);
  }, [latestGens]);

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10">
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
        <aside className="rounded-lg border p-4 h-fit sticky top-24">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={auth.currentUser?.photoURL || undefined} />
              <AvatarFallback>{(auth.currentUser?.email?.[0] || 'U').toUpperCase()}</AvatarFallback>
            </Avatar>
            <div>
              <div className="text-sm font-semibold truncate max-w-[140px]">{auth.currentUser?.displayName || auth.currentUser?.email}</div>
              <div className="text-xs text-muted-foreground capitalize">{userDoc?.subscription_tier || 'free'}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full mb-4" onClick={async()=>{ try{ await signOut(auth); navigate('/auth'); } catch{} }}>Se déconnecter</Button>
          <nav className="space-y-1 text-sm">
            <button className={`w-full text-left px-3 py-2 rounded ${active==='profile'?'bg-accent':'hover:bg-accent/50'}`} onClick={()=>setActive('profile')}>Profil</button>
            <button className={`w-full text-left px-3 py-2 rounded ${active==='activity'?'bg-accent':'hover:bg-accent/50'}`} onClick={()=>setActive('activity')}>Activité</button>
            <button className={`w-full text-left px-3 py-2 rounded ${active==='subscription'?'bg-accent':'hover:bg-accent/50'}`} onClick={()=>setActive('subscription')}>Abonnement</button>
          </nav>
        </aside>

        <main className="space-y-6">
          {active === 'profile' && (
            <>
              <Card>
                <CardHeader><CardTitle>Résumé du compte</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <div className="text-sm text-muted-foreground">Crédits restants</div>
                      <div className="text-3xl font-bold">{userDoc?.credits ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Crédits utilisés (10 dernières)</div>
                      <div className="text-3xl font-bold">{creditsUsed}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Plan</div>
                      <div className="text-3xl font-bold capitalize">{userDoc?.subscription_tier || 'free'}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {active === 'activity' && (
            <>
              <Card>
                <CardHeader><CardTitle>Générations - période {timeRange}</CardTitle></CardHeader>
                <CardContent>
                  {(() => {
                    const buckets: Record<string, number> = {};
                    for (const g of latestGens) {
                      const d = g.created_at?.toDate ? g.created_at.toDate() : (g.created_at || new Date());
                      const dt = new Date(d);
                      // Granularité automatique basée sur la période choisie
                      const mode: 'daily'|'monthly'|'yearly' = ((): any => {
                        if (timeRange === '10d' || timeRange === '30d') return 'daily';
                        if (timeRange === '3m' || timeRange === '6m' || timeRange === '1y') return 'monthly';
                        return 'daily';
                      })();
                      const key = mode === 'daily'
                        ? dt.toISOString().slice(0,10)
                        : mode === 'monthly'
                        ? `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`
                        : `${dt.getFullYear()}`;
                      buckets[key] = (buckets[key] || 0) + 1;
                    }
                    const data = Object.entries(buckets).sort(([a],[b])=>a.localeCompare(b)).map(([date, count])=>({ date, count }));
                    return (
                      <ChartContainer config={{ count: { label: 'Gen', color: 'hsl(262.1 83.3% 57.8%)' } }} className="w-full">
                        <LineChart data={data} margin={{ left: 12, right: 12, top: 12, bottom: 12 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="count" stroke="var(--color-count)" strokeWidth={2} dot={false} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                        </LineChart>
                      </ChartContainer>
                    );
                  })()}
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <span className="text-xs text-muted-foreground">Période:</span>
                    <Button size="sm" variant={timeRange==='10d'?'secondary':'ghost'} onClick={()=>setTimeRange('10d')}>10 jours</Button>
                    <Button size="sm" variant={timeRange==='30d'?'secondary':'ghost'} onClick={()=>setTimeRange('30d')}>30 jours</Button>
                    <Button size="sm" variant={timeRange==='3m'?'secondary':'ghost'} onClick={()=>setTimeRange('3m')}>3 mois</Button>
                    <Button size="sm" variant={timeRange==='6m'?'secondary':'ghost'} onClick={()=>setTimeRange('6m')}>6 mois</Button>
                    <Button size="sm" variant={timeRange==='1y'?'secondary':'ghost'} onClick={()=>setTimeRange('1y')}>1 an</Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Historique récent</CardTitle></CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {latestGens.map(g => {
                      const title = g.button_title || (typeof g.button_id === 'string' ? (buttonTitles[g.button_id] || '—') : '—');
                      const preset = String(g.upscale_preset || 'original');
                      const presetLabel = preset === 'auto_4k' ? '4K' : preset === 'auto_2k' ? '2K' : 'Original';
                      const buttonCost = typeof g.button_cost === 'number' ? g.button_cost : (typeof baseCosts[g.button_id] === 'number' ? baseCosts[g.button_id] : 0);
                      const upscaleCost = typeof g.upscale_cost === 'number' ? g.upscale_cost : computeUpscaleCredits((g.upscale_preset || 'original'));
                      const totalCredits = typeof g.total_cost === 'number' ? g.total_cost : (typeof g.credits_cost === 'number' ? g.credits_cost : (buttonCost + upscaleCost));
                      return (
                        <div key={g.id} className="flex items-center justify-between py-2 text-sm">
                          <div className="truncate mr-3">{title} - {presetLabel}</div>
                          <div className="font-mono text-muted-foreground">{totalCredits} crédits</div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {active === 'subscription' && (
            <Card>
              <CardHeader><CardTitle>Abonnement</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 text-sm mb-6">
                  <div>Plan actuel:</div>
                  <div className="px-2 py-0.5 rounded border capitalize">{userDoc?.subscription_tier || 'free'}</div>
                </div>

                {/* Jauge de crédits */}
                <div className="rounded-lg border p-4 mb-6">
                  <div className="text-sm text-muted-foreground mb-2">Jauge de crédits</div>
                  <div className="h-2 w-full bg-muted rounded">
                    {(() => {
                      const max = 5000; // borne visuelle (cap affichage)
                      const cur = Math.min(Math.max(Number(userDoc?.credits || 0), 0), max);
                      return <div className="h-2 bg-primary rounded" style={{ width: `${(cur/max)*100}%` }} />
                    })()}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">{userDoc?.credits ?? 0} / 5000</div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {/* Basic */}
                  <div className="rounded-lg border p-5 flex flex-col h-full">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-lg font-semibold">Basic</div>
                      <div className="text-sm font-bold bg-transparent text-blue-600 px-3 py-1.5 rounded-lg border border-white">0€ / mois</div>
                    </div>
                    <ul className="text-sm list-disc pl-4 space-y-1 text-muted-foreground mb-4 flex-grow">
                      <li>0 crédit / mois</li>
                      <li>Utilisation limitée</li>
                      <li>Éditeur sur images uploadées</li>
                    </ul>
                    <div className="mt-auto">
                      {userDoc?.subscription_tier === 'free' ? (
                        <Button disabled className="w-full" variant="secondary">Actif</Button>
                      ) : (
                        <Button variant="outline" className="w-full" onClick={()=>setShowChangePlan({ plan:'free', mode:'downgrade' })}>Downgrade</Button>
                      )}
                    </div>
                  </div>

                  {/* Classic */}
                  <div className="rounded-lg border p-5 flex flex-col h-full">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-lg font-semibold">Classic</div>
                      <div className="text-sm font-bold bg-transparent text-blue-600 px-3 py-1.5 rounded-lg border border-white">9,99€ / mois</div>
                    </div>
                    <ul className="text-sm list-disc pl-4 space-y-1 text-muted-foreground mb-4 flex-grow">
                      <li>+150 crédits / mois</li>
                      <li>Upscale 2K/4K</li>
                      <li>1 tâche concurrente</li>
                    </ul>
                    <div className="mt-auto">
                      {userDoc?.subscription_tier === 'classic' ? (
                        <Button disabled className="w-full" variant="secondary">Actif</Button>
                      ) : (
                        <Button className="w-full" onClick={async()=>{
                          try {
                            if (userDoc?.subscription_tier && userDoc.subscription_tier !== 'free') {
                              setShowChangePlan({ plan:'classic', mode: userDoc.subscription_tier==='pro' ? 'downgrade' : 'upgrade' });
                            } else {
                              const baseUrl = location.hostname === 'localhost' ? `http://localhost:${location.port || '5173'}` : location.origin;
                              const r = await fetch(getFirebaseFunctionUrl('createCheckoutSession'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ plan:'classic', uid: auth.currentUser?.uid, email: auth.currentUser?.email, successUrl: `${baseUrl}/account?success=true&plan=classic`, cancelUrl: `${baseUrl}/account` }) });
                              const j = await r.json();
                              if (!r.ok) throw new Error(j?.error||'Checkout error');
                              location.href = j.url;
                            }
                          } catch(e:any) { toast({ title:'Erreur', description: e?.message || 'Impossible de démarrer le paiement', variant:'destructive' }); }
                        }}>{userDoc?.subscription_tier==='pro'?'Downgrade':'Upgrade'}</Button>
                      )}
                    </div>
                  </div>

                  {/* Pro */}
                  <div className="rounded-lg border p-5 flex flex-col h-full">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-lg font-semibold">Pro</div>
                      <div className="text-sm font-bold bg-transparent text-blue-600 px-3 py-1.5 rounded-lg border border-yellow-400 flex items-center gap-1">
                        ⭐ 29,99€ / mois
                      </div>
                    </div>
                    <ul className="text-sm list-disc pl-4 space-y-1 text-muted-foreground mb-4 flex-grow">
                      <li>+400 crédits / mois</li>
                      <li>Priorité de traitement</li>
                      <li>Jusqu'à 2 tâches concurrentes</li>
                    </ul>
                    <div className="mt-auto">
                      {userDoc?.subscription_tier === 'pro' ? (
                        <Button disabled className="w-full" variant="secondary">Actif</Button>
                      ) : (
                        <Button className="w-full" onClick={async()=>{
                          try {
                            if (userDoc?.subscription_tier && userDoc.subscription_tier !== 'free') {
                              setShowChangePlan({ plan:'pro', mode: 'upgrade' });
                            } else {
                              const baseUrl = location.hostname === 'localhost' ? `http://localhost:${location.port || '8080'}` : location.origin;
                              const r = await fetch(getFirebaseFunctionUrl('createCheckoutSession'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ plan:'pro', uid: auth.currentUser?.uid, email: auth.currentUser?.email, successUrl: `${baseUrl}/account?success=true&plan=pro`, cancelUrl: `${baseUrl}/account` }) });
                              const j = await r.json();
                              if (!r.ok) throw new Error(j?.error||'Checkout error');
                              location.href = j.url;
                            }
                          } catch(e:any) { toast({ title:'Erreur', description: e?.message || 'Impossible de démarrer le paiement', variant:'destructive' }); }
                        }}>{userDoc?.subscription_tier==='classic'?'Upgrade':'Upgrade'}</Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Billing & Payment summary + portal */}
                <div className="rounded-lg border p-5 mt-6">
                  <div className="text-lg font-semibold mb-3">Facturation & paiement</div>
                  <div className="grid sm:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center justify-between sm:block">
                      <div className="text-muted-foreground">Prix</div>
                      <div className="font-medium">
                        {(() => {
                          const tier = String(userDoc?.subscription_tier || 'free');
                          if (tier === 'free') return '—';
                          const amount: number | undefined = (userDoc?.subscription_price_amount ?? undefined);
                          const currency: string | undefined = (userDoc?.subscription_price_currency ?? undefined);
                          if (typeof amount === 'number' && currency) {
                            return new Intl.NumberFormat(undefined, { style:'currency', currency: currency.toUpperCase() }).format(amount);
                          }
                          return tier==='pro' ? '29,99€ / mo' : tier==='classic' ? '9,99€ / mo' : '—';
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:block">
                      <div className="text-muted-foreground">Période de facturation</div>
                      <div className="font-medium capitalize">{(() => {
                        const tier = String(userDoc?.subscription_tier || 'free');
                        if (tier === 'free') return '—';
                        const v = String(userDoc?.subscription_interval || '')
                        if (v === 'month') return 'mensuel';
                        if (v === 'year') return 'annuel';
                        return v || '—';
                      })()}</div>
                    </div>
                    <div className="flex items-center justify-between sm:block">
                      <div className="text-muted-foreground">Prochain prélèvement</div>
                      <div className="font-medium">
                        {(() => {
                          const tier = String(userDoc?.subscription_tier || 'free');
                          if (tier === 'free') return '—';
                          return nextChargeMs ? new Date(nextChargeMs).toLocaleString('fr-FR', { dateStyle:'long', timeStyle:'short' }) : '—';
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* Historique de facturation */}
                  <div className="mt-6">
                    <div className="text-sm text-muted-foreground mb-2">Historique de facturation</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b">
                            <th className="py-2 pr-4">Période</th>
                            <th className="py-2 pr-4">Montant</th>
                            <th className="py-2 pr-4">Statut</th>
                            <th className="py-2 pr-4">Facture</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billingRows.length === 0 && (
                            <tr><td colSpan={4} className="py-3 text-muted-foreground">Aucune facture</td></tr>
                          )}
                          {billingRows.map((r) => {
                            const start = r.period_start?.toDate ? r.period_start.toDate() : null;
                            const end = r.period_end?.toDate ? r.period_end.toDate() : null;
                            const amount = typeof r.amount_paid === 'number' ? new Intl.NumberFormat(undefined, { style:'currency', currency: String(r.currency||'eur').toUpperCase() }).format(r.amount_paid) : '—';
                            return (
                              <tr key={r.id} className="border-b last:border-0">
                                <td className="py-2 pr-4">{start && end ? `${start.toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' })} → ${end.toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' })}` : '—'}</td>
                                <td className="py-2 pr-4">{amount}</td>
                                <td className="py-2 pr-4 capitalize">{String(r.status || '—')}</td>
                                <td className="py-2 pr-4">
                                  {r.hosted_invoice_url ? (
                                    <a className="text-primary underline" href={r.hosted_invoice_url} target="_blank" rel="noreferrer">ouvrir</a>
                                  ) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button variant="outline" onClick={async()=>{
                      try {
                        const customerId = userDoc?.stripe_customer_id;
                        if (!customerId) throw new Error('Aucun client Stripe');
                        const baseUrl = location.hostname === 'localhost' ? `http://localhost:${location.port || '5173'}` : location.origin;
                        const r = await fetch(getFirebaseFunctionUrl('createPortalSession'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ customerId, returnUrl: `${baseUrl}/account` }) });
                        const j = await r.json();
                        if (!r.ok) throw new Error(j?.error||'Portal error');
                        location.href = j.url;
                      } catch(e:any) { toast({ title:'Erreur', description: e?.message || 'Impossible d’ouvrir le portail', variant:'destructive' }); }
                    }}>Éditer le paiement</Button>
                  </div>
                </div>

                {/* Change plan dialog */}
                <Dialog open={!!showChangePlan} onOpenChange={(o)=>!o&&setShowChangePlan(null)}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {showChangePlan?.mode==='upgrade' ? 'Améliorer l’abonnement' : 'Réduire l’abonnement'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-muted-foreground space-y-2">
                      {showChangePlan?.mode==='upgrade' ? (
                        <>
                          <p>Souhaitez-vous appliquer le changement maintenant (proration immédiate) ou au prochain renouvellement ?</p>
                        </>
                      ) : (
                        <p>Les réductions de plan s’appliquent au prochain renouvellement.</p>
                      )}
                    </div>
                    <DialogFooter className="flex flex-wrap gap-2">
                      {showChangePlan?.mode==='upgrade' && (
                        <Button disabled={changing} onClick={async()=>{
                          if (!auth.currentUser || !showChangePlan) return;
                          setChanging(true);
                          try {
                            const r = await fetch(getFirebaseFunctionUrl('changeSubscriptionPlan'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ uid: auth.currentUser.uid, plan: showChangePlan.plan, timing: 'now' }) });
                            const j = await r.json();
                            if (!r.ok) throw new Error(j?.error||'Change failed');
                            toast({ title:'Abonnement mis à jour', description:'Le changement est effectif immédiatement.' });
                            setShowChangePlan(null);
                          } catch(e:any){ toast({ title:'Erreur', description: e?.message || 'Impossible de modifier l’abonnement', variant:'destructive' }); } finally { setChanging(false);} 
                        }}>Appliquer maintenant</Button>
                      )}
                      <Button variant="secondary" disabled={changing} onClick={async()=>{
                        if (!auth.currentUser || !showChangePlan) return;
                        setChanging(true);
                        try {
                          const r = await fetch(getFirebaseFunctionUrl('changeSubscriptionPlan'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ uid: auth.currentUser.uid, plan: showChangePlan.plan, timing: 'period_end' }) });
                          const j = await r.json();
                          if (!r.ok) throw new Error(j?.error||'Change failed');
                          toast({ title:'Changement programmé', description:'Le plan changera au prochain renouvellement.' });
                          setShowChangePlan(null);
                        } catch(e:any){ toast({ title:'Erreur', description: e?.message || 'Impossible de programmer le changement', variant:'destructive' }); } finally { setChanging(false);} 
                      }}>{showChangePlan?.mode==='upgrade' ? 'Prochain renouvellement' : 'Confirmer'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
};

export default Account;


